import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { translateDomainError } from "#app/i18n/translateApiError";
import { showErrorToast } from "#app/notifications";
import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Loader } from "#components/atoms/Loader/Loader";
import { Switch } from "#components/atoms/Switch/Switch";
import { Text } from "#components/atoms/Typographies/Text";
import { DropdownMenu } from "#components/molecules/DropdownMenu/DropdownMenu";
import {
  SidePanelContent,
  SidePanelFooter,
  SidePanelHeader,
} from "#components/molecules/SidePanel/SidePanel";
import { useApplyLayoutNesting } from "#modules/layouts/view/useApplyLayoutNesting";
import { useLayout } from "#modules/layouts/view/useLayout";
import { useLayoutNesting } from "#modules/layouts/view/useLayoutNesting";
import {
  buildNestingInputsDefaultValues,
  NestingInputsFormInput,
  NestingInputsFormSchema,
  NestingInputsFormValues,
} from "#modules/layoutsV2/domain/nestingInputsFormSchema";
import { NestingInputs } from "#modules/layoutsV2/view/NestingInputs";
import { NestingItem } from "#modules/layoutsV2/view/NestingItemsTable";
import { NestingPlan } from "#modules/layoutsV2/view/NestingPlan";
import { getOptimizationGain } from "#modules/optimizations/domain/getOptimizationGain";
import { getOptimizationMaxPartDimensions } from "#modules/optimizations/domain/getOptimizationMaxPartDimensions";
import {
  getOptimizationSubtitle,
  getOptimizationTitle,
} from "#modules/optimizations/domain/optimizationCardContent";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { invalidateOptimizedMaterialCosts } from "#modules/optimizations/infra/invalidateOptimizedMaterialCosts";
import { CancelOptimizationModal } from "#modules/optimizations/view/CancelOptimizationModal";
import { useExtractOptimizationLine } from "#modules/optimizations/view/useExtractOptimizationLine";
import { useRunOptimizationNesting } from "#modules/optimizations/view/useRunOptimizationNesting";
import { useToggleOptimization } from "#modules/optimizations/view/useToggleOptimization";
import { useUpdateOptimizationNesting } from "#modules/optimizations/view/useUpdateOptimizationNesting";
import { useUpdateLayoutMaterial } from "#modules/quoteRequestLines/views/DetailsPanel/Material/useUpdateLayoutMaterial";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import {
  computeMaterialPrice,
  computeNestingMaterialGain,
  computeNestingUnitMaterialCosts,
  DEFAULT_SCRAP_BILLING_RATE,
  formatPrice,
  getNestedLineQuantity,
  isDomainError,
  isJobProcessing,
  isNestingRunApplied,
  JobStatusSchema,
  MaterialGradeSchema,
  NO_OPTIMIZED_MATERIAL_COSTS,
} from "shared";

type Props = {
  optimization: OptimizationDto;
  onBack: () => void;
  onRequestBack: () => void;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

const parseDecimal = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
};

const toNestingItems = (
  optimization: OptimizationDto,
  unitMaterialCosts: Map<string, number>,
): NestingItem[] =>
  [...optimization.quoteRequestLines]
    .sort(
      (left, right) =>
        new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime() ||
        left.id.localeCompare(right.id),
    )
    .map((line) => ({
      id: line.id,
      part: line.part,
      quantity: getNestedLineQuantity(line),
      unitMaterialCost: unitMaterialCosts.get(line.id) ?? null,
    }));

export const NestingOptimizationDetail = ({
  optimization,
  onBack,
  onRequestBack,
  onUnsavedChangesChange,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editedBilledShare, setEditedBilledShare] = useState<string | null>(null);
  const [committedBilledShare, setCommittedBilledShare] = useState<number | null>(null);
  const { requestExtract, confirmExtract, cancelExtract, isConfirmOpen, isExtractPending } =
    useExtractOptimizationLine({ optimization, onCancelled: onBack });
  const {
    runOptimizationNesting,
    data: startedNestingRun,
    isPending,
    isError,
  } = useRunOptimizationNesting({
    quoteRequestId: optimization.quoteRequestId,
    optimizationId: optimization.id,
    onError: (error) =>
      showErrorToast({
        title: isDomainError(error) ? translateDomainError(error) : t("layout.errors.nestingError"),
      }),
  });
  const layoutId =
    optimization.sheetNestingOptimizationMetadata?.layoutId ?? startedNestingRun?.layoutId ?? null;

  const { nestingRun, isSuccess: isNestingRunFetched } = useLayoutNesting(layoutId);
  const { layout, isFetching: isFetchingLayout } = useLayout(layoutId ?? "");
  const { updateOptimizationNesting, isPending: isUpdatingBilledShare } =
    useUpdateOptimizationNesting({
      quoteRequestId: optimization.quoteRequestId,
      lineIds: optimization.quoteRequestLines.map(({ id }) => id),
    });

  const metadata = optimization.sheetNestingOptimizationMetadata;
  const [firstLine] = optimization.quoteRequestLines;
  const savedCutWidth = layout?.cutWidth ?? null;
  const savedSkeletonWidth = layout?.skeletonWidth ?? null;
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty: hasEditedInputs },
  } = useForm<NestingInputsFormInput, unknown, NestingInputsFormValues>({
    defaultValues: buildNestingInputsDefaultValues({
      cutWidth: savedCutWidth,
      skeletonWidth: savedSkeletonWidth,
    }),
    resolver: zodResolver(NestingInputsFormSchema),
  });
  const { updateLayoutMaterial, isPending: isUpdatingMaterial } = useUpdateLayoutMaterial({
    materialLayout: layout?.materialLayout ?? null,
    materialGrade: metadata?.materialGrade ?? MaterialGradeSchema.enum.OTHER,
    quoteRequestLineId: undefined,
  });
  const resetFormToSavedWidths = useEffectEvent(() =>
    reset(
      buildNestingInputsDefaultValues({
        cutWidth: savedCutWidth,
        skeletonWidth: savedSkeletonWidth,
      }),
    ),
  );

  useEffect(() => {
    resetFormToSavedWidths();
  }, [savedCutWidth, savedSkeletonWidth]);

  const hasNoNestingRun = layoutId === null || (isNestingRunFetched && !nestingRun);
  const hasAutoRun = useRef(false);
  const runInitialNesting = useEffectEvent(() =>
    runOptimizationNesting({ optimizationId: optimization.id }),
  );

  useEffect(() => {
    if (hasNoNestingRun && !hasAutoRun.current) {
      hasAutoRun.current = true;
      runInitialNesting();
    }
  }, [hasNoNestingRun]);

  const isNestingInProgress = nestingRun ? isJobProcessing(nestingRun.status) : false;
  const isLoading = isPending || isNestingInProgress || (hasNoNestingRun && !isError);
  const isMaterialCostLoading = isLoading || isUpdatingBilledShare || isFetchingLayout;

  const nestedLines = optimization.quoteRequestLines.map((line) => ({
    id: line.id,
    quantity: getNestedLineQuantity(line),
  }));
  const savedBilledShare = layout?.newScrapMaterialBillingRate ?? DEFAULT_SCRAP_BILLING_RATE;
  const billedShare = editedBilledShare ?? String(savedBilledShare);
  const simulatedBilledShare = committedBilledShare ?? savedBilledShare;
  const hasEditedBilledShare = simulatedBilledShare !== savedBilledShare;

  const unitMaterialCosts = layout
    ? computeNestingUnitMaterialCosts({
        nestingRun,
        layout: { ...layout, newScrapMaterialBillingRate: simulatedBilledShare },
        lines: nestedLines,
      })
    : new Map<string, number>();

  const commitBilledShare = () => {
    const newScrapMaterialBillingRate = parseDecimal(billedShare);
    if (newScrapMaterialBillingRate !== null) {
      setCommittedBilledShare(newScrapMaterialBillingRate);
    }
  };

  const runNesting = ({ cutWidth, skeletonWidth }: NestingInputsFormValues) =>
    runOptimizationNesting({
      optimizationId: optimization.id,
      cutWidth,
      skeletonWidth,
      newScrapMaterialBillingRate: simulatedBilledShare,
    });

  const calculate = (values: NestingInputsFormValues) => {
    if (values.material) {
      updateLayoutMaterial(values.material, { onSuccess: () => runNesting(values) });
      return;
    }
    runNesting(values);
  };

  const applyToQuoteId = useId();
  const [isAppliedToQuote, setIsAppliedToQuote] = useState(optimization.enabled);
  const { toggleOptimization, isPending: isTogglePending } = useToggleOptimization({
    quoteRequestId: optimization.quoteRequestId,
  });
  const { applyLayoutNesting, isPending: isApplyingNesting } = useApplyLayoutNesting(
    layoutId ?? "",
    null,
    {
      onSuccess: () =>
        invalidateOptimizedMaterialCosts(
          queryClient,
          optimization.quoteRequestId,
          optimization.quoteRequestLines.map(({ id }) => id),
        ),
      onError: (error) =>
        showErrorToast({
          title: isDomainError(error)
            ? translateDomainError(error)
            : t("layout.errors.nestingError"),
        }),
    },
  );

  const currentUnitCosts = new Map(
    optimization.quoteRequestLines.flatMap((line) =>
      line.layout
        ? [
            [
              line.id,
              computeMaterialPrice(
                {
                  quoteRequestLine: {
                    ...line,
                    layout: { ...line.layout, newScrapMaterialBillingRate: simulatedBilledShare },
                  },
                  optimizedMaterialCosts: NO_OPTIMIZED_MATERIAL_COSTS,
                },
                computationOptions,
              ).partMaterialCost,
            ] as const,
          ]
        : [],
    ),
  );
  const estimatedGain = computeNestingMaterialGain({
    lines: nestedLines,
    currentUnitCosts,
    nestedUnitCosts: unitMaterialCosts,
  });
  const hasGain = estimatedGain !== null && estimatedGain > 0;
  const hasAutoApplied = useRef(getOptimizationGain(optimization).isCalculated);
  const autoApplyToQuote = useEffectEvent(() => setIsAppliedToQuote(true));

  useEffect(() => {
    const shouldAutoApply = hasGain && !isMaterialCostLoading && !hasAutoApplied.current;
    if (shouldAutoApply) {
      hasAutoApplied.current = true;
      autoApplyToQuote();
    }
  }, [hasGain, isMaterialCostLoading]);

  const hasUnsavedChanges =
    isAppliedToQuote !== optimization.enabled || hasEditedInputs || hasEditedBilledShare;
  const isNestingProcessed = nestingRun?.status === JobStatusSchema.enum.PROCESSED;

  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  const toggleThenBack = () => {
    if (isAppliedToQuote === optimization.enabled) {
      onBack();
      return;
    }
    toggleOptimization(
      { optimizationId: optimization.id, enabled: isAppliedToQuote },
      { onSuccess: onBack },
    );
  };

  const syncBilledShare = (onSuccess: () => void) => {
    if (!hasEditedBilledShare) {
      onSuccess();
      return;
    }
    updateOptimizationNesting(
      {
        optimizationId: optimization.id,
        newScrapMaterialBillingRate: simulatedBilledShare,
        syncQuoteRequestLines: isAppliedToQuote,
      },
      { onSuccess },
    );
  };

  const finish = () => {
    const shouldApplyNesting =
      isAppliedToQuote && isNestingProcessed && layout && !isNestingRunApplied(layout, nestingRun);
    if (shouldApplyNesting) {
      applyLayoutNesting(undefined, { onSuccess: toggleThenBack });
      return;
    }
    toggleThenBack();
  };

  const onFinish = () => syncBilledShare(finish);

  return (
    <>
      <SidePanelHeader
        title={getOptimizationTitle(optimization, t)}
        subtitle={getOptimizationSubtitle(optimization, t)}
        onBack={onRequestBack}
        backText={t("optimizations.detail.back")}
        onClose={onRequestBack}
        closeText={t("common.close")}
      />
      <SidePanelContent>
        <Flex direction="column" gap={spacing[8]} h="100%">
          <NestingInputs
            materialLayout={layout?.materialLayout ?? null}
            partType={firstLine?.part.type ?? null}
            partSubType={firstLine?.part.subType ?? null}
            materialGrade={metadata?.materialGrade ?? MaterialGradeSchema.enum.OTHER}
            materialName={metadata?.materialName ?? ""}
            partDimensionsWithOverthickness={getOptimizationMaxPartDimensions(optimization)}
            control={control}
            isCalculating={isPending || isUpdatingMaterial}
            isNestingInProgress={isNestingInProgress}
            isDirty={hasEditedInputs}
            onCalculate={handleSubmit(calculate)}
          />
          <NestingPlan
            items={toNestingItems(optimization, unitMaterialCosts)}
            nestingRun={nestingRun}
            isLoading={isLoading}
            billedShare={billedShare}
            onBilledShareChange={setEditedBilledShare}
            onBilledShareBlur={commitBilledShare}
            isMaterialCostLoading={isMaterialCostLoading}
            renderRowAction={(itemId) => (
              <DropdownMenu position="bottom-end">
                <DropdownMenu.Target>
                  <IconButton
                    variant="transparent"
                    size="sm"
                    aria-label={t("optimizations.detail.rowActions")}
                  >
                    <Icon name="ellipsis" size={16} />
                  </IconButton>
                </DropdownMenu.Target>
                <DropdownMenu.Dropdown>
                  <DropdownMenu.Item
                    leftSection={
                      <Icon name="layers3" size={16} color={colors.icon.primaryDefault} />
                    }
                    disabled={isExtractPending}
                    onClick={() => requestExtract(itemId)}
                  >
                    {t("optimizations.detail.extractFromNesting")}
                  </DropdownMenu.Item>
                </DropdownMenu.Dropdown>
              </DropdownMenu>
            )}
          />
        </Flex>
      </SidePanelContent>
      <SidePanelFooter>
        <Flex align="center" justify="space-between" w="100%" gap={spacing[4]}>
          <Flex align="center" gap={spacing[2]}>
            {isMaterialCostLoading ? (
              <Loader size="small" />
            ) : (
              <Text size="small" type="medium" color={colors.text.success}>
                {t("optimizations.detail.estimatedGainLabel", {
                  gain: formatPrice(estimatedGain, " €", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                })}
              </Text>
            )}
          </Flex>
          <Flex align="center" gap={spacing[6]} style={{ flexShrink: 0 }}>
            <Flex align="center" gap={spacing[2]}>
              <label htmlFor={applyToQuoteId} style={{ cursor: "pointer" }}>
                <Text size="small" color={colors.text.secondaryDefault} component="span">
                  {t("optimizations.detail.applyToQuote")}
                </Text>
              </label>
              <Switch
                id={applyToQuoteId}
                checked={isAppliedToQuote && hasGain}
                disabled={!hasGain}
                onChange={setIsAppliedToQuote}
              />
            </Flex>
            <Button
              variant="primary"
              size="lg"
              onClick={onFinish}
              loading={isTogglePending || isApplyingNesting || isUpdatingBilledShare}
              disabled={isMaterialCostLoading}
            >
              {t("optimizations.done")}
            </Button>
          </Flex>
        </Flex>
      </SidePanelFooter>
      <CancelOptimizationModal
        opened={isConfirmOpen}
        isConfirming={isExtractPending}
        onConfirm={confirmExtract}
        onKeepEditing={cancelExtract}
      />
    </>
  );
};
